import { Dispatch, useState } from 'react'
import { useQuery } from 'react-query'
import { ErrorResponse, MemberList, Member, AccountApi } from '@citruscamps/citrus-client'
import { UnlimitedPagination } from '../constants/pagination'
import { Pagination } from '../interfaces/pagination'
import { generateApiConfig } from '../utils/client-config'
import { generateListKey } from '../utils/key-generator'
import { useRequestHandler } from './useRequestHandler'
import { useSearchValue } from './useSearchValue'

const DefaultFetchProps: FetchProps = {
  sort: 'created',
  order: 'ASC',
}

interface FetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
}

interface IProps {
  pagination?: Pagination
  fetchProps?: FetchProps
  overwriteFilters?: boolean
  enabled?: boolean
  redirect?: boolean
}

interface FetchUserMembers {
  data: Member[]
  error: ErrorResponse | null
  fetchProps: FetchProps
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  search: string
  setFetchProps: (value?: FetchProps) => void
  setPagination: Dispatch<Pagination>
  setSearch: Dispatch<string>
}

export const useFetchUserMembers = ({
  pagination: initialPagination,
  overwriteFilters,
  enabled = true,
  redirect,
  ...props
}: IProps): FetchUserMembers => {
  const { requestHandler } = useRequestHandler()
  const [pagination, setPagination] = useState<Pagination>(initialPagination || UnlimitedPagination)
  const initialFetchProps: FetchProps = props.fetchProps || DefaultFetchProps
  const [fetchProps, setFetchProps] = useState<FetchProps>(initialFetchProps)
  const { search, deferSearch, setSearch, isSearching } = useSearchValue('', {
    onDefer: () => setPagination(initialPagination || UnlimitedPagination),
  })
  const queryKeys = generateListKey({
    type: 'member',
    program_id: undefined,
    pagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: {},
    search: deferSearch,
  })
  const {
    isLoading,
    isError,
    data: resp,
    error,
  } = useQuery<MemberList, ErrorResponse>(
    queryKeys,
    async ({ signal }) => {
      const client = new AccountApi(generateApiConfig())
      const skip = pagination.limit ? pagination.page * pagination.limit : undefined
      return await requestHandler<MemberList>(
        () =>
          client.findMembersByUserProgramAccount(
            {
              limit: pagination.limit,
              skip,
              order: fetchProps.order,
              sort: fetchProps.sort,
            },
            { signal },
          ),
        { redirect },
      )
    },
    {
      enabled,
    },
  )

  const _setFetchProps = (val?: FetchProps) => {
    setPagination(initialPagination || UnlimitedPagination)
    return setFetchProps(val || DefaultFetchProps)
  }

  return {
    data: resp?.data || [],
    error,
    fetchProps,
    isError,
    isLoading: isLoading || isSearching,
    pagination: {
      ...pagination,
      count: resp?.count || pagination.count,
      total: resp?.total || pagination.total,
      page: resp?.page || pagination.page,
      page_count: resp?.page_count || pagination.page_count,
    },
    search,
    setFetchProps: _setFetchProps,
    setPagination,
    setSearch,
  }
}
